// Explore-stats page: Parliamentary office holders (#219).
// Aggregate view across every office captured under recordsofservice
// ROSTypeID=8 (Parliamentary Positions): Speaker, President, Chairman
// of Committees, Member of the Speaker's Panel, Deputy Chairman of
// Committees, etc. Surfaces per-office leaderboards, average term
// length, turnover per decade, and overall party composition.
//
// Distinct from the per-office cuts at #200 (Speakers) and #201
// (Presidents) — this is the unified roll-up. Wendy / Andrew suggested
// the timeline drill-down; we render it as a per-office stat-card grid
// with a "top 5 longest holders" list per office (linked through to
// /parliamentarians/:phid).

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack, Link, Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, partyColor } from './charts';

function OfficeCard({ summary, top }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6">{summary.office}</Typography>
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
          {summary.distinct_holders} holders · {summary.tenures} tenures ·
          mean term {summary.mean_term_years} yrs · longest{' '}
          {summary.longest_years} yrs ({summary.longest_holder})
        </Typography>
        {top && top.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {top.map((h) => (
              <Box key={h.phid + h.name}
                   sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, fontSize: 13 }}>
                  {h.phid ? (
                    <Link component={RouterLink}
                          to={`/parliamentarians/${h.phid}`}
                          underline="hover">{h.name}</Link>
                  ) : h.name}
                </Box>
                <Chip size="small" label={h.party || 'Independent'}
                      sx={{ bgcolor: partyColor(h.party),
                             color: '#fff', fontSize: 10 }} />
                <Box sx={{ minWidth: 50, textAlign: 'right',
                            fontSize: 12 }}>
                  {h.years} yrs
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function OfficeHolders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('office-holders');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const offices = c.offices || [];
  const perOfficeTop = c.per_office_top || {};

  return (
    <ExploreStatsPage
      title="Parliamentary office holders"
      description="Every captured parliamentary office under `recordsofservice` ROSTypeID=8 (Parliamentary Positions) — Speaker, President, Chairman of Committees, Deputy Chairman of Committees, Member of the Speaker's Panel, and so on. Sourced from the same backbone the per-office Speakers (#200) and Presidents (#201) pages use; this is the unified roll-up. Multiple tenures per holder are merged so cumulative tenure is correct. Party is the directory's current party (the best the source data supports for historic office-holders)."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="office-holders"
    >
      {data && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Office totals (cumulative tenure across all holders)
                </Typography>
                <StatBar data={offices.map((o) => ({
                  office: o.office, years: o.total_years,
                }))} xKey="office" yKey="years" layout="vertical"
                         height={Math.max(280, 24 * offices.length + 80)} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Average term length per office (years)
                </Typography>
                <StatBar data={offices.map((o) => ({
                  office: o.office, mean: o.mean_term_years,
                }))} xKey="office" yKey="mean" layout="vertical"
                         height={Math.max(280, 24 * offices.length + 80)} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  New office-takings per decade (turnover proxy)
                </Typography>
                <StatBar data={c.per_decade || []} xKey="decade"
                         yKey="count" height={320} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Office-holder cumulative years by party
                </Typography>
                <StatBar data={c.party_composition || []} xKey="party"
                         yKey="years" layout="vertical" height={320} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                Office leaderboards — top 5 longest holders per office
              </Typography>
            </Grid>
            {offices.map((o) => (
              <Grid item xs={12} md={6} key={o.office}>
                <OfficeCard summary={o} top={perOfficeTop[o.office] || []} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
